import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

/** Remove barra final para prefixos comparáveis entre envs. */
export function normalizeApiOrigin(raw: string | undefined): string {
  if (!raw) return ''
  return raw.replace(/\/+$/, '')
}

const API_URL = normalizeApiOrigin(process.env.REACT_APP_API_URL)
const FORM_URL = normalizeApiOrigin(process.env.REACT_APP_FORM_URL)

export function getApiBaseOrigins(): { primary: string; alternate: string } | null {
  if (!API_URL || !FORM_URL || API_URL === FORM_URL) return null
  return { primary: API_URL, alternate: FORM_URL }
}

/**
 * Se a URL absoluta começa por uma das bases conhecidas, devolve a mesma rota na outra base.
 */
export function swapApiBaseOrigin(absoluteUrl: string): string | null {
  const bases = getApiBaseOrigins()
  if (!bases) return null
  const u = absoluteUrl
  if (u.startsWith(bases.primary)) {
    return bases.alternate + u.slice(bases.primary.length)
  }
  if (u.startsWith(bases.alternate)) {
    return bases.primary + u.slice(bases.alternate.length)
  }
  return null
}

function isNetworkStyleError(error: { code?: string; response?: unknown; request?: unknown }): boolean {
  const code = error.code
  return (
    code === 'ERR_NETWORK' ||
    code === 'ERR_CONNECTION_REFUSED' ||
    code === 'ERR_CONNECTION_CLOSED' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ECONNRESET' ||
    (!error.response && !!error.request)
  )
}

function isServerUnavailable(error: { response?: { status?: number } }): boolean {
  const s = error.response?.status
  return s === 502 || s === 503 || s === 504
}

/**
 * Só métodos seguros/idempotentes: em CORS o servidor pode ter processado a requisição;
 * repetir POST/PUT/PATCH na outra base poderia duplicar efeito.
 */
function allowsAlternateBaseForMethod(method: string | undefined): boolean {
  const m = (method || 'get').toUpperCase()
  return m === 'GET' || m === 'HEAD' || m === 'DELETE'
}

export function shouldTryAlternateApiBase(error: {
  code?: string
  response?: { status?: number }
  request?: unknown
  config?: { method?: string }
}): boolean {
  if (!getApiBaseOrigins()) return false
  if (!allowsAlternateBaseForMethod(error.config?.method)) return false
  return isNetworkStyleError(error) || isServerUnavailable(error)
}

/** Evita loop: uma troca de base por requisição lógica. */
export const API_BASE_FALLBACK_FLAG = '__apiBaseFallbackDone' as const

type FallbackConfig = InternalAxiosRequestConfig & {
  [API_BASE_FALLBACK_FLAG]?: boolean
}

/**
 * Em falha de rede ou 502/503/504, repete a requisição uma vez trocando
 * REACT_APP_API_URL ↔ REACT_APP_FORM_URL (mesmo path).
 * Registre depois dos demais handlers de erro se quiser que este rode primeiro (LIFO do Axios).
 */
export function installApiBaseFallback(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const cfg = error.config as FallbackConfig | undefined
      if (!cfg || cfg[API_BASE_FALLBACK_FLAG]) {
        return Promise.reject(error)
      }
      if (!shouldTryAlternateApiBase(error)) {
        return Promise.reject(error)
      }
      let full: string
      try {
        full = axios.getUri(cfg)
      } catch {
        return Promise.reject(error)
      }
      const nextUrl = swapApiBaseOrigin(full)
      if (!nextUrl) {
        return Promise.reject(error)
      }
      cfg[API_BASE_FALLBACK_FLAG] = true
      cfg.baseURL = ''
      cfg.url = nextUrl
      return instance.request(cfg) as Promise<AxiosResponse>
    },
  )
}
